import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AddInvoiceToOrderResponse,
  BulkGenerateInvoicesResponse,
  GenerateInvoiceApplicationErrors,
  GenerateInvoiceResponse,
  Invoice,
  ListInvoicesForMultipleOrdersResponse,
  addInvoiceToOrder as universalAddInvoiceToOrder,
  bulkGenerateInvoices as universalBulkGenerateInvoices,
  generateInvoice as universalGenerateInvoice,
  listInvoicesForMultipleOrders as universalListInvoicesForMultipleOrders,
} from './ecom-orders-v1-invoice-order-invoices.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ecom' };

export function listInvoicesForMultipleOrders(
  httpClient: HttpClient
): ListInvoicesForMultipleOrdersSignature {
  return (orderIds: string[]) =>
    universalListInvoicesForMultipleOrders(
      orderIds,
      // @ts-ignore
      { httpClient }
    );
}

interface ListInvoicesForMultipleOrdersSignature {
  /**
   * Retrieves the IDs of invoices associated with all specified orders.
   *
   *
   * The `listInvoicesForSingleOrder()` function returns a Promise that resolves when the specified order's transaction records are retrieved.
   * @param - Order IDs for which to retrieve invoices.
   */
  (orderIds: string[]): Promise<
    NonNullablePaths<
      ListInvoicesForMultipleOrdersResponse,
      `invoicesForOrder` | `invoicesForOrder.${number}.orderId`,
      4
    >
  >;
}

/** @internal */
export function generateInvoice(
  httpClient: HttpClient
): GenerateInvoiceSignature {
  return (orderId: string) =>
    universalGenerateInvoice(
      orderId,
      // @ts-ignore
      { httpClient }
    );
}

interface GenerateInvoiceSignature {
  /**
   * Generates and adds an invoice to a specified order.
   * @param - Order ID.
   */
  (orderId: string): Promise<
    NonNullablePaths<GenerateInvoiceResponse, `invoiceId`, 2> & {
      __applicationErrorsType?: GenerateInvoiceApplicationErrors;
    }
  >;
}

/** @internal */
export function bulkGenerateInvoices(
  httpClient: HttpClient
): BulkGenerateInvoicesSignature {
  return (orderIds: string[]) =>
    universalBulkGenerateInvoices(
      orderIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkGenerateInvoicesSignature {
  /**
   * Generates and adds invoices to all specified orders.
   * @param - Order IDs.
   */
  (orderIds: string[]): Promise<
    NonNullablePaths<
      BulkGenerateInvoicesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.orderId`
      | `results.${number}.item.invoiceId`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

/** @internal */
export function addInvoiceToOrder(
  httpClient: HttpClient
): AddInvoiceToOrderSignature {
  return (
    orderId: string,
    invoiceInfo: NonNullablePaths<Invoice, `_id` | `appId`, 2>
  ) =>
    universalAddInvoiceToOrder(
      orderId,
      invoiceInfo,
      // @ts-ignore
      { httpClient }
    );
}

interface AddInvoiceToOrderSignature {
  /**
   * Adds an invoice to a specified order.
   * @param - Order ID.
   * @param - Invoice info.
   */
  (
    orderId: string,
    invoiceInfo: NonNullablePaths<Invoice, `_id` | `appId`, 2>
  ): Promise<
    NonNullablePaths<
      AddInvoiceToOrderResponse,
      | `orderInvoices`
      | `orderInvoices.${number}._id`
      | `orderInvoices.${number}.appId`,
      4
    >
  >;
}

export {
  AccountInfo,
  ActionEvent,
  AddInvoiceToOrderRequest,
  AddInvoiceToOrderResponse,
  ApplicationError,
  BulkActionMetadata,
  BulkGenerateInvoicesRequest,
  BulkGenerateInvoicesResponse,
  BulkInvoiceResult,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  GenerateInvoiceRequest,
  GenerateInvoiceResponse,
  GenerateInvoiceWithNumberRequest,
  GenerateInvoiceWithNumberResponse,
  GetOrderInvoiceRequest,
  GetOrderInvoiceResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  Invoice,
  InvoiceForOrder,
  InvoicesForOrder,
  ItemMetadata,
  ListInvoicesForMultipleOrdersRequest,
  ListInvoicesForMultipleOrdersResponse,
  ListInvoicesForSingleOrderRequest,
  ListInvoicesForSingleOrderResponse,
  MessageEnvelope,
  RestoreInfo,
  WebhookIdentityType,
} from './ecom-orders-v1-invoice-order-invoices.universal.js';
